import React from "react";
import shortid from "shortid";
import produce from "immer";

import {Product} from "../product/types";

import {CartItem, Context, State, Actions, Cart} from "./types";
import {getOrderId} from "./selectors";
import api from "./api";

import {useAnalytics} from "~/analytics/hooks";
import {useTenant} from "~/tenant/hooks";
import {Field} from "~/tenant/types";

interface Props {
  children: JSX.Element | JSX.Element[];
}

const CartContext = React.createContext({} as Context);

const CartProvider = ({children}: Props) => {
  const log = useAnalytics();
  const {phone, hook, title} = useTenant();
  const [cart, setCart] = React.useState<Cart>({});
  const items = React.useMemo(() => [].concat(...Object.values(cart)), [cart]);

  function add(product: Product, count: number = 1, note: string = "") {
    log.addToCart(product);

    return setCart(
      produce((cart) => {
        const id = shortid.generate();

        cart[id] = {
          id,
          count,
          product,
          note,
        };
      }),
    );
  }

  function remove(id: CartItem["id"]) {
    if (!cart[id]) return;

    log.removeFromCart(cart[id].product);

    return setCart(
      produce((cart) => {
        delete cart[id];
      }),
    );
  }

  function increase(id: CartItem["id"]) {
    if (!cart[id]) return;

    return setCart(
      produce((cart) => {
        cart[id].count++;
      }),
    );
  }

  function decrease(id: CartItem["id"]) {
    if (!cart[id]) return;

    if (cart[id].count === 1) {
      return remove(id);
    }

    return setCart(
      produce((cart) => {
        cart[id].count--;
      }),
    );
  }

  async function checkout(fields?: Field[]) {
    // We generate an order id
    const orderId = getOrderId(title);

    // We need to create the window reference before because Safari doesn't let us execute a window.open after an async operation
    let tab = window.open("", "_blank") || window;

    // Log to analytics
    log.checkout(items);

    // If a webhook is configured
    if (hook) {
      try {
        // Do a post to it
        api.hook(hook, {phone, items, orderId, fields});
      } catch (e) {
        // If we had an error log it to the console
        console.warn("Hook was not dispatched: ", e);
      }
    }

    // Redirect the user to whatsapp
    tab.location.href = api.checkout({phone, items, orderId, fields});
  }

  const state: State = {items, cart};
  const actions: Actions = {add, remove, checkout, increase, decrease};

  return <CartContext.Provider value={{state, actions}}>{children}</CartContext.Provider>;
};

export {CartProvider as Provider, CartContext as default};
